import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { useDebounce } from 'use-lodash-debounce'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import { Dropdown, Input } from 'semantic-ui-react'
import TableComponent from '~/components/TableComponent'
import dayjs from 'dayjs'
import i18n from '~/components/i18n'
import { FilterWrapper } from '../MilkSamples'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Tour data id'),
    key: 'uniqueId',
    sorted: 'desc'
  },
  {
    name: i18n.t('Date'),
    key: 'date',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'desc'
  },
  {
    name: i18n.t('Date start'),
    type: 'date',
    format: 'HH:mm:ss',
    key: 'date'
  },
  {
    name: i18n.t('Date end'),
    type: 'date',
    format: 'HH:mm:ss',
    key: 'dateEnd'
  },
  {
    name: i18n.t('Amount accepted (in l)'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('avg. Temp. (°C)'),
    key: 'avgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  },
  {
    name: i18n.t('Sample bottle'),
    key: 'sampleBottleNumber'
  },
  {
    name: i18n.t('Sample Compartment Temp (°C)'),
    key: 'sampleAvgTemp',
    type: 'no-unit-number',
    decimalScale: 1,
    sorted: 'desc'
  }
]

const MilkMoney: React.FC = () => {
  const { t } = useTranslation()
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    column: '',
    sort: '',
    amountFrom: '',
    amountTo: '',
    month: ''
  })
  const [amountData, setAmountData] = useState({
    amountFrom: '',
    amountTo: '',
    deliveryMonth: ''
  })
  const debouncedTotalAmount = useDebounce(amountData, 300)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())

  const {
    isLoading: isBusyData,
    data: milkDeliveryData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getFarmerMilkDeliveries({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: milkDeliveryOptions,
    isLoading: isBusyOptions,
    refetch: fetchOptions
  } = MilkMoneyQuery.getFarmerMilkOptions()

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchOptions()
  }, [t])

  useEffect(() => {
    fetchFilteredData();
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, page: 0, ...debouncedTotalAmount });
  }, [debouncedTotalAmount]);

  function renderFilters() {
    return <FilterWrapper>
      <Dropdown
        selectOnBlur={false}
        placeholder={t('Delivery month tour')}
        selection
        value={dataFiltered.month}
        clearable
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, month: data.value as string })}
        options={milkDeliveryOptions?.months?.map(option => ({ text: dayjs(option.label).format('MMM YYYY'), value: option.key }))}
      />
      <Input
        icon={{ name: 'sort numeric down', circular: true, link: true }}
        placeholder={t('Total amount from (in l)')}
        value={amountData.amountFrom}
        type='number'
        onKeyPress={(event) => {
          if (!/[0-9]/.test(event.key)) {
            event.preventDefault();
          }
        }}
        onChange={(e, data) => setAmountData({ ...amountData, amountFrom: data.value })}
      />
      <Input
        icon={{ name: 'sort numeric up', circular: true, link: true }}
        placeholder={t('Total amount to (in l)')}
        value={amountData.amountTo}
        onKeyPress={(event) => {
          if (!/[0-9]/.test(event.key)) {
            event.preventDefault();
          }
        }}
        type='number'
        onChange={(e, data) => setAmountData({ ...amountData, amountTo: data.value })}
      />
    </FilterWrapper>
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  const { filtered, rows } = milkDeliveryData ? milkDeliveryData : { filtered: 0, rows: [] };

  return <Layout
    isBusy={isBusyData || isBusyOptions}
    segmented
    title={t('Milk delivery')}
    filters={renderFilters()}
  >
    {renderTable()}
  </Layout>
}
export default MilkMoney